/**
 * Questions fréquentes.
 *
 * Elles ne sont pas un ornement : une page de prestation publiée avec moins de
 * trois questions **fait échouer la publication du site**. L'écran affiche donc
 * en tête la couverture de chaque prestation, prise du même rapport que celui
 * qui bloquera le build, et permet d'atterrir directement sur celle qui manque.
 */
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import {
  ContenuAtelier,
  PiedFormulaire,
  Section,
  type Selection,
  champsModifies,
  erreurChamp,
  useFiche,
} from "../composants/ContenuAtelier";
import {
  type Question,
  type ScopeFaq,
  useCommunes,
  useEnregistrer,
  usePrestations,
  useQualite,
  useQuestions,
  useSupprimer,
} from "../composants/ContenuDonnees";
import { EditeurRiche } from "../composants/EditeurRiche";

type Brouillon = {
  question: string;
  reponse: string;
  scope: ScopeFaq;
  /** `""` vaut « aucune cible » ; l'API attend `null`. */
  scopeId: number | "";
  ordre: number;
  isPublished: boolean;
};

const VIDE: Brouillon = {
  question: "",
  reponse: "",
  scope: "globale",
  scopeId: "",
  ordre: 0,
  isPublished: true,
};

const LIBELLES_SCOPE: Record<ScopeFaq, string> = {
  globale: "Tout le site",
  prestation: "Une prestation",
  commune: "Une commune",
};

function depuis(question: Question): Brouillon {
  return {
    question: question.question,
    reponse: question.reponse,
    scope: question.scope,
    scopeId: question.scopeId ?? "",
    ordre: question.ordre,
    isPublished: question.isPublished,
  };
}

function pourApi(champs: Record<string, unknown>): Record<string, unknown> {
  const converti = { ...champs };
  if ("scopeId" in converti) {
    converti.scopeId = converti.scopeId === "" ? null : Number(converti.scopeId);
  }
  return converti;
}

export function ContenuFaq() {
  const questions = useQuestions();
  const prestations = usePrestations();
  const communes = useCommunes();
  const enregistrement = useEnregistrer<Question>("faq");
  const suppression = useSupprimer("faq");
  const fiche = useFiche<Brouillon>(VIDE);

  const [filtre, setFiltre] = useState<ScopeFaq | "toutes">("toutes");

  const liste = questions.data ?? [];
  const visibles = filtre === "toutes" ? liste : liste.filter((q) => q.scope === filtre);

  function cible(question: Question): string {
    if (question.scope === "globale") return "Tout le site";
    const source = question.scope === "prestation" ? prestations.data : communes.data;
    const trouve = source?.find((element) => element.id === question.scopeId);
    const nom = trouve && "nom" in trouve ? trouve.nom : `#${question.scopeId}`;
    return `${LIBELLES_SCOPE[question.scope]} — ${nom}`;
  }

  function selectionner(selection: Selection) {
    enregistrement.reset();
    suppression.reset();

    if (selection === null) return fiche.fermer();
    if (selection === "nouveau") return fiche.ouvrir("nouveau", VIDE);

    const question = liste.find((element) => element.id === selection);
    if (question) fiche.ouvrir(selection, depuis(question));
  }

  function enregistrer() {
    const id = typeof fiche.selection === "number" ? fiche.selection : null;
    const champs =
      id === null ? { ...fiche.brouillon } : champsModifies(fiche.brouillon, fiche.reference);
    if (Object.keys(champs).length === 0) return;

    enregistrement.mutate(
      { id, champs: pourApi(champs) },
      {
        onSuccess: (question) => {
          if (question) fiche.confirmer(question.id, depuis(question));
        },
      },
    );
  }

  function supprimer() {
    if (typeof fiche.selection !== "number") return;
    suppression.mutate(fiche.selection, { onSuccess: () => fiche.fermer() });
  }

  const erreur = enregistrement.error ?? suppression.error ?? null;
  const brouillon = fiche.brouillon;
  const cibles =
    brouillon.scope === "prestation" ? (prestations.data ?? []) : (communes.data ?? []);

  return (
    <ContenuAtelier
      intro="Les questions publiées alimentent les blocs FAQ des pages et le balisage FAQPage. Trois questions au minimum par page de prestation publiée."
      entete={
        <Stack spacing={2}>
          <CouverturePrestations />
          <FiltrePortee valeur={filtre} surChangement={setFiltre} />
        </Stack>
      }
      entrees={visibles.map((question) => ({
        id: question.id,
        libelle: question.question,
        secondaire: cible(question),
        publie: question.isPublished,
      }))}
      selection={fiche.selection}
      surSelection={selectionner}
      libelleNouveau="Nouvelle question"
      modifie={fiche.modifie}
      chargement={questions.isPending}
      erreur={questions.error ?? null}
      invite="Choisissez une question à modifier, ou ajoutez-en une."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={4}>
          <Section titre="Question">
            <TextField
              fullWidth
              label="Question"
              value={brouillon.question}
              onChange={(evenement) => fiche.modifier({ question: evenement.target.value })}
              error={erreurChamp(erreur, "question") !== undefined}
              helperText={
                erreurChamp(erreur, "question") ??
                "Formulée comme un visiteur la poserait, pas comme un titre de rubrique."
              }
            />

            <EditeurRiche
              libelle="Réponse"
              valeur={brouillon.reponse}
              surChangement={(html) => fiche.modifier({ reponse: html })}
              hauteurMin={180}
              aide="Une réponse complète et autonome : elle peut être citée seule, hors de la page."
            />
          </Section>

          <Section titre="Rattachement">
            <FormControl fullWidth>
              <InputLabel id="etiquette-portee">Portée</InputLabel>
              <Select
                labelId="etiquette-portee"
                label="Portée"
                value={brouillon.scope}
                onChange={(evenement) => {
                  const scope = evenement.target.value as ScopeFaq;
                  // Changer de portée invalide la cible : une prestation n'est
                  // pas une commune, et garder l'ancien identifiant rattacherait
                  // la question à n'importe quoi.
                  fiche.modifier({ scope, scopeId: "" });
                }}
              >
                {(Object.keys(LIBELLES_SCOPE) as ScopeFaq[]).map((scope) => (
                  <MenuItem key={scope} value={scope}>
                    {LIBELLES_SCOPE[scope]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {brouillon.scope !== "globale" && (
              <FormControl fullWidth error={erreurChamp(erreur, "scopeId") !== undefined}>
                <InputLabel id="etiquette-cible">
                  {brouillon.scope === "prestation" ? "Prestation" : "Commune"}
                </InputLabel>
                <Select<number | "">
                  labelId="etiquette-cible"
                  label={brouillon.scope === "prestation" ? "Prestation" : "Commune"}
                  value={brouillon.scopeId}
                  onChange={(evenement) =>
                    fiche.modifier({
                      scopeId: evenement.target.value === "" ? "" : Number(evenement.target.value),
                    })
                  }
                >
                  {cibles.map((element) => (
                    <MenuItem key={element.id} value={element.id}>
                      {element.nom}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {erreurChamp(erreur, "scopeId")}
                </Typography>
              </FormControl>
            )}

            <TextField
              type="number"
              label="Ordre d'affichage"
              value={brouillon.ordre}
              onChange={(evenement) =>
                fiche.modifier({ ordre: Number(evenement.target.value) || 0 })
              }
              sx={{ maxWidth: 220 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={brouillon.isPublished}
                  onChange={(evenement) =>
                    fiche.modifier({ isPublished: evenement.target.checked })
                  }
                />
              }
              label="Publier cette question"
            />
            <Typography variant="caption" color="text.secondary">
              Seules les questions publiées comptent dans le décompte des trois questions exigées
              par page de prestation.
            </Typography>
          </Section>
        </Stack>

        <PiedFormulaire
          modifie={fiche.modifie}
          enregistrement={enregistrement.isPending}
          surEnregistrer={enregistrer}
          surSupprimer={typeof fiche.selection === "number" ? supprimer : undefined}
          suppression={suppression.isPending}
          quoi="cette question"
          erreur={erreur}
          succes={fiche.succes}
        />
      </Paper>
    </ContenuAtelier>
  );
}

/** Filtre de la liste par portée. */
function FiltrePortee({
  valeur,
  surChangement,
}: {
  valeur: ScopeFaq | "toutes";
  surChangement: (valeur: ScopeFaq | "toutes") => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={valeur}
      onChange={(_evenement, choix) => {
        // `null` signifie « on vient de désélectionner le bouton actif » : sans
        // ce garde-fou, la liste se retrouverait filtrée sur rien du tout.
        if (choix !== null) surChangement(choix as ScopeFaq | "toutes");
      }}
      aria-label="Filtrer par portée"
    >
      <ToggleButton value="toutes">Toutes</ToggleButton>
      <ToggleButton value="globale">Site</ToggleButton>
      <ToggleButton value="prestation">Prestations</ToggleButton>
      <ToggleButton value="commune">Communes</ToggleButton>
    </ToggleButtonGroup>
  );
}

/**
 * Couverture par prestation, lue dans le rapport de qualité : c'est le même
 * décompte que celui appliqué à la publication, jamais un recomptage local qui
 * pourrait s'en écarter.
 */
function CouverturePrestations() {
  const qualite = useQualite();

  if (qualite.isPending || qualite.isError) return null;

  const { seuils } = qualite.data;
  const pages = qualite.data.pages.filter((page) => page.type === "prestation");
  if (pages.length === 0) return null;

  const bloquantes = pages.filter((page) => page.publiee && page.nombreFaq < seuils.faqMin);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Questions publiées par prestation (minimum {seuils.faqMin})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {pages.map((page) => {
            const suffisant = page.nombreFaq >= seuils.faqMin;
            return (
              <Chip
                key={page.chemin}
                size="small"
                label={`${page.titre} : ${page.nombreFaq}`}
                color={suffisant ? "success" : page.publiee ? "error" : "warning"}
                variant={suffisant ? "outlined" : "filled"}
              />
            );
          })}
        </Stack>
      </Paper>

      {bloquantes.length > 0 && (
        <Alert severity="error">
          {bloquantes.length} page{bloquantes.length > 1 ? "s" : ""} publiée
          {bloquantes.length > 1 ? "s" : ""} n'atteint pas les {seuils.faqMin} questions exigées :
          la publication du site échouera tant qu'il en manque.
        </Alert>
      )}
    </Stack>
  );
}
